/* eslint-disable react/function-component-definition */
import { Button } from "primereact/button";
import { Calendar } from "primereact/calendar";
import { Checkbox } from "primereact/checkbox";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { Message } from "primereact/message";
import { Toast } from "primereact/toast";
import React, { useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";

import {
  useGetAllBaseRateFilesQuery,
  useUploadBaseRateFileMutation,
} from "../../../store/queries/baseRateFiles";
import { IBaseRateFile } from "../../../types/baseRateFile";

/**
 * Local-noon calendar date avoids UTC day-boundary bugs from PrimeReact Calendar.
 */
function localDateForCalendar(
  y: number,
  monthIndex: number,
  day: number
): Date {
  return new Date(y, monthIndex, day, 12, 0, 0, 0);
}

function todayForCalendar(): Date {
  const n = new Date();
  return localDateForCalendar(n.getFullYear(), n.getMonth(), n.getDate());
}

function calendarSelectionToLocalDay(
  value: Date | null | undefined
): Date | null {
  if (
    value == null ||
    !(value instanceof Date) ||
    Number.isNaN(value.getTime())
  ) {
    return null;
  }
  return localDateForCalendar(
    value.getFullYear(),
    value.getMonth(),
    value.getDate()
  );
}

function toIsoDateOnly(value: Date | null): string | undefined {
  if (!value || !(value instanceof Date) || Number.isNaN(value.getTime())) {
    return undefined;
  }
  const y = value.getFullYear();
  const m = String(value.getMonth() + 1).padStart(2, "0");
  const day = String(value.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

const VALIDATION_PREFIX = "Base rate file validation failed:";

function parseBaseRateValidationErrors(
  message: string | undefined
): string[] | null {
  if (!message || !message.startsWith(VALIDATION_PREFIX)) return null;
  return message
    .slice(VALIDATION_PREFIX.length)
    .split("\n")
    .map((l) => l.trim())
    .filter(Boolean);
}

function baseRateDisplayNameLabel(raw: string): string {
  return raw.trim();
}

function findConflictingBaseRateByDisplayName(
  rows: IBaseRateFile[],
  inputDisplayName: string,
  isCarveout: boolean
): IBaseRateFile | undefined {
  const label = baseRateDisplayNameLabel(inputDisplayName);
  if (!label) return undefined;
  return rows.find((f) => {
    if ((f.isCarveout === true) !== isCarveout) return false;
    return baseRateDisplayNameLabel(f.displayName) === label;
  });
}

const StyledDialog = styled(Dialog)`
  .selected-files-list {
    border: 1px solid #dee2e6;
    border-radius: 4px;
    padding: 0.5rem;
    margin: 1rem 0;
    max-height: 200px;
    overflow-y: auto;

    .selected-file-item {
      display: flex;
      align-items: center;
      padding: 0.5rem;
      border-bottom: 1px solid #f8f9fa;

      &:last-child {
        border-bottom: none;
      }

      .file-name {
        flex: 1;
        margin-right: 1rem;
      }

      .remove-file-btn {
        background: transparent;
        border: none;
        color: #6c757d;
        width: 2rem;
        height: 2rem;
        padding: 0;

        &:hover {
          background: transparent;
          color: #495057;
        }

        &:focus {
          box-shadow: none;
        }

        .p-button-icon {
          font-size: 1rem;
        }
      }
    }
  }

  .file-upload-actions {
    display: flex;
    justify-content: space-between;
    margin-top: 1rem;
  }
`;

interface IBaseRateFileUploadDialogProps {
  visible: boolean;
  onHide: () => void;
  accountId?: string;
  /** When true, the "Carveout file" checkbox is pre-checked and disabled. */
  forceCarveout?: boolean;
  /** Called after a successful upload with the newly created file. */
  onUploaded?: (file: IBaseRateFile) => void;
}

export const BaseRateFileUploadDialog: React.FC<
  IBaseRateFileUploadDialogProps
> = ({ visible, onHide, accountId, forceCarveout, onUploaded }) => {
  const toast = useRef<Toast>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [displayName, setDisplayName] = useState("");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadEffectiveDate, setUploadEffectiveDate] = useState<Date | null>(
    () => todayForCalendar()
  );
  const [uploadIsCarveout, setUploadIsCarveout] = useState<boolean>(
    forceCarveout === true
  );
  const [isUploading, setIsUploading] = useState(false);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);

  const { data: baseRateFilesData } = useGetAllBaseRateFilesQuery({
    accountId,
  });
  const [uploadFile] = useUploadBaseRateFileMutation();

  const baseRateFiles = baseRateFilesData?.data ?? [];

  useEffect(() => {
    if (visible) {
      setUploadIsCarveout(forceCarveout === true);
    }
  }, [visible, forceCarveout]);

  const resetForm = () => {
    setDisplayName("");
    setSelectedFile(null);
    setUploadEffectiveDate(todayForCalendar());
    setUploadIsCarveout(forceCarveout === true);
    setValidationErrors([]);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleHide = () => {
    if (isUploading) return;
    resetForm();
    onHide();
  };

  const uploadFilenameClash = useMemo(() => {
    if (!selectedFile) return undefined;
    return baseRateFiles.find(
      (f) =>
        f.fileName === selectedFile.name &&
        (f.isCarveout === true) === uploadIsCarveout
    );
  }, [selectedFile, baseRateFiles, uploadIsCarveout]);

  const uploadDisplayNameConflict = useMemo(() => {
    if (!visible) return undefined;
    return findConflictingBaseRateByDisplayName(
      baseRateFiles,
      displayName,
      uploadIsCarveout
    );
  }, [visible, baseRateFiles, displayName, uploadIsCarveout]);

  const handleUpload = async () => {
    if (!displayName || !selectedFile) {
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: "Please provide a display name and select a file",
      });
      return;
    }

    if (!uploadEffectiveDate) {
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: "Please select an effective date for this base rate term.",
      });
      return;
    }
    const uploadIso = toIsoDateOnly(uploadEffectiveDate);
    if (!uploadIso) {
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: "Please select an effective date for this base rate term.",
      });
      return;
    }

    if (uploadFilenameClash) {
      toast.current?.show({
        severity: "warn",
        summary: "File name already exists",
        detail:
          "A file with this name already exists for this type. Use Update on it from the Base Rate Files page instead.",
      });
      return;
    }

    if (uploadDisplayNameConflict) {
      toast.current?.show({
        severity: "error",
        summary: "Display name already in use",
        detail: `Another base rate file in this account already uses this display name (file: ${uploadDisplayNameConflict.fileName}). Choose a distinct label.`,
      });
      return;
    }

    setIsUploading(true);

    try {
      const result = await uploadFile({
        file: selectedFile,
        displayName,
        accountId,
        effectiveDate: uploadIso,
        ...(uploadIsCarveout ? { isCarveout: true } : {}),
      }).unwrap();

      // CLE-2796 follow-up — the file was accepted but some rows will be skipped (a blank rate
      // is a code the payer set no price for). Say so, or the smaller generated count later
      // looks like a bug.
      const warnings = result?.data?.metadata?.integrity?.warnings ?? [];
      if (warnings.length > 0) {
        toast.current?.show({
          severity: "warn",
          summary: "Uploaded, some rows skipped",
          detail: warnings.join(" "),
          life: 12000,
        });
      } else {
        toast.current?.show({
          severity: "success",
          summary: "Success",
          detail: "File uploaded successfully",
        });
      }

      if (result?.data) {
        onUploaded?.(result.data);
      }

      resetForm();
      onHide();
    } catch (error: unknown) {
      const fetchErr = error as {
        data?: { message?: string; messages?: string[] };
      };
      const errText = fetchErr.data?.message ?? fetchErr.data?.messages?.[0];
      const parsed = parseBaseRateValidationErrors(errText);
      if (parsed) {
        setValidationErrors(parsed);
      } else {
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: errText ?? "Failed to upload files",
        });
      }
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <>
      <Toast ref={toast} />
      <StyledDialog
        header={
          forceCarveout
            ? "Upload New Carveout File"
            : "Upload New Base Rate File"
        }
        visible={visible}
        onHide={handleHide}
        style={{ width: "600px" }}
        closable={!isUploading}
        closeOnEscape={!isUploading}
      >
        <div className="p-fluid">
          <div className="field">
            <label htmlFor="uploadDialogDisplayName">Display Name *</label>
            <InputText
              id="uploadDialogDisplayName"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="Enter a name for these base rate files"
            />
            {uploadDisplayNameConflict && (
              <Message
                severity="error"
                className="w-full mt-2"
                text={`Display name must be unique per account among logical files. Already used by “${uploadDisplayNameConflict.displayName}” (${uploadDisplayNameConflict.fileName}).`}
              />
            )}
          </div>
          <div className="field">
            <label htmlFor="uploadDialogEffectiveDate">
              Term effective date *
            </label>
            <Calendar
              id="uploadDialogEffectiveDate"
              value={uploadEffectiveDate}
              onChange={(e) =>
                setUploadEffectiveDate(
                  calendarSelectionToLocalDay(e.value as Date | null)
                )
              }
              showIcon
              className="w-full"
              dateFormat="mm-dd-yy"
            />
          </div>
          <div className="field-checkbox">
            <Checkbox
              inputId="uploadDialogIsCarveout"
              checked={uploadIsCarveout}
              disabled={forceCarveout === true}
              onChange={(e) => setUploadIsCarveout(e.checked === true)}
            />
            <label htmlFor="uploadDialogIsCarveout" className="ml-2">
              Carveout file
            </label>
          </div>
          <div className="field">
            <label>Select CSV file *</label>
            <input
              type="file"
              ref={fileInputRef}
              accept=".csv"
              onChange={(e) => {
                if (e.target.files && e.target.files[0]) {
                  setSelectedFile(e.target.files[0]);
                  setValidationErrors([]);
                  e.target.value = "";
                }
              }}
              style={{ display: "none" }}
              id="base-rate-upload-dialog-file-input"
            />
            <Button
              label="Choose File"
              icon="pi pi-plus"
              className="p-button-outlined mb-3"
              onClick={() =>
                document
                  .getElementById("base-rate-upload-dialog-file-input")
                  ?.click()
              }
            />
            {selectedFile && (
              <>
                <div className="selected-files-list">
                  <div className="selected-file-item">
                    <span className="file-name">{selectedFile.name}</span>
                    <Button
                      icon="pi pi-times"
                      className="p-button-text p-button-plain remove-file-btn"
                      onClick={() => {
                        setSelectedFile(null);
                      }}
                    />
                  </div>
                </div>
                {uploadFilenameClash && (
                  <div className="mt-2">
                    <Message
                      severity="warn"
                      className="w-full"
                      text="A base rate file with this file name already exists. Use Update on that file from the Base Rate Files page instead of a fresh upload."
                    />
                  </div>
                )}
                {validationErrors.length > 0 && (
                  <div className="mt-3 p-3 border-round surface-100 border-1 border-red-300">
                    <div className="flex align-items-center gap-2 mb-2 text-red-700 font-semibold">
                      <i className="pi pi-exclamation-triangle" />
                      File validation failed
                    </div>
                    <ul className="m-0 pl-4 text-sm line-height-3">
                      {validationErrors.map((err, i) => (
                        <li key={i} className="mb-1 text-red-700">
                          {err}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                <div className="file-upload-actions">
                  <Button
                    label="Cancel"
                    icon="pi pi-times"
                    className="p-button-text"
                    onClick={() => {
                      setSelectedFile(null);
                      if (fileInputRef.current) {
                        fileInputRef.current.value = "";
                      }
                    }}
                  />
                  <Button
                    label="Upload"
                    icon={
                      isUploading ? "pi pi-spin pi-spinner" : "pi pi-upload"
                    }
                    onClick={handleUpload}
                    disabled={
                      isUploading ||
                      !!uploadFilenameClash ||
                      !!uploadDisplayNameConflict
                    }
                    loading={isUploading}
                  />
                </div>
              </>
            )}
          </div>
        </div>
      </StyledDialog>
    </>
  );
};
